import * as React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { stableSort } from "../../../../utils/stable-sort";
import { descendingComparator } from "../../../../utils/descending-comparator";
import { useDispatch, useSelector } from "react-redux";
import { receiptsActions, ReceiptState } from "../../store";
import {
  ReceiptItem,
  ReceiptNF,
} from "../../../../../domain/models/receipt-model";
import { EnhancedTableHead } from "./Components/enhanced-table-head";
import { EnhancedTableToolbar } from "./Components/enhanced-table-toolbar";

type Data = Record<string, any>;

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
  sortable?: boolean;
}
const headCells: HeadCell[] = [
  {
    id: "nf",
    numeric: false,
    disablePadding: true,
    label: "Nota Fiscal",
    sortable: true,
  },
  {
    id: "code",
    numeric: true,
    disablePadding: false,
    label: "EAN",
    sortable: true,
  },
  {
    id: "name",
    numeric: true,
    disablePadding: false,
    label: "Produto",
    sortable: true,
  },
  {
    id: "unit",
    numeric: true,
    disablePadding: false,
    label: "Unidade",
    sortable: true,
  },
  {
    id: "amount",
    numeric: true,
    disablePadding: false,
    label: "Quantidade",
    sortable: true,
  },
];

export default function NewReceiptsTable() {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("calories");
  const [selected, setSelected] = React.useState<readonly Data[]>([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [rows, setRows] = React.useState<Data[]>([]);

  const dispatch = useDispatch();

  const nfs: ReceiptNF[] = useSelector<ReceiptState, ReceiptNF[]>(
    (state) => state.data
  );

  React.useEffect(() => {
    const rows: ReceiptItem[] = [];
    nfs.map((nf) => {
      nf.items.map((item) => {
        const newItem = Object.assign({}, item, { nf: nf.number });
        rows.push(newItem);
      });
    });
    setRows(rows);
  }, [nfs]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              headCells={headCells}
            />
            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow hover tabIndex={-1} key={index}>
                      {headCells?.map((head, index) => {
                        return index === 0 ? (
                          <TableCell
                            component="th"
                            id={labelId}
                            scope="row"
                            key={index}
                            sx={{
                              color: `${row.registered ? "" : "red"}`,
                            }}
                          >
                            {row[head.id]}
                          </TableCell>
                        ) : (
                          <TableCell
                            key={index}
                            align="right"
                            sx={{
                              color: `${row.registered ? "" : "red"}`,
                            }}
                          >
                            {row[head.id]}{" "}
                          </TableCell>
                        );
                      })}
                      <>
                        <TableCell align="right">
                          {row.batchControl && (
                            <TextField
                              disabled={!!row.insertBatchLater}
                              size="small"
                              sx={{
                                width: "100px",
                                "& .MuiInputBase-input": {
                                  py: "0",
                                },
                              }}
                              value={row.batch ? row.batch : ""}
                              onChange={(e) => {
                                dispatch(
                                  receiptsActions.saveBatch({
                                    batch: e.target.value,
                                    nfNumber: row.nf.toString(),
                                    nItem: row.nItem.toString(),
                                  })
                                );
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row.batchControl && (
                            <Checkbox
                              checked={row.insertBatchLater}
                              onChange={(e) => {
                                dispatch(
                                  receiptsActions.toggleBatchOption({
                                    nfNumber: row.nf.toString(),
                                    nItem: row.nItem.toString(),
                                    value: e.target.checked,
                                  })
                                );
                              }}
                            />
                          )}
                        </TableCell>
                      </>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Itens por página"
          component="div"
          count={rows.length}
          labelDisplayedRows={({ from, to, count }): any => {
            return `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`;
          }}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Comprimir"
      />
    </Box>
  );
}
